import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import StudentProfileView from './StudentProfileView';
import AssignStudentsDialog from '@/components/admin/AssignStudentsDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PlusCircle, Upload, Users as UsersIcon, Trash2, Loader2, AlertTriangle, Eye, EyeOff, Search, Filter, ArrowUpRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function AdminUsers({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'student',
    first_name: '',
    last_name: '',
    mobile_number: '',
    school_name: '',
    city: '',
    grade: '',
    custom_fields: {}
  });
  const [newTeacher, setNewTeacher] = useState({
    email: '',
    password: '',
    role: 'teacher',
    first_name: '',
    last_name: ''
  });
  const [newMentor, setNewMentor] = useState({
    email: '',
    password: '',
    role: 'mentor',
    first_name: '',
    last_name: ''
  });
  const [file, setFile] = useState(null);
  const [mentorFile, setMentorFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingMentor, setUploadingMentor] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 50;
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('registration_date'); // Can be 'registration_date' or 'last_login'
  const [lastLoginFilter, setLastLoginFilter] = useState('all');
  
  const [customFields, setCustomFields] = useState([]);
  const [loadingCustomFields, setLoadingCustomFields] = useState(true);
  
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showProfileView, setShowProfileView] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedMentorForAssignment, setSelectedMentorForAssignment] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchCustomFields();
  }, []);
  
  const fetchCustomFields = async () => {
    try {
      const res = await axios.get(`${API}/custom-fields?include_hidden=true`, { headers: getAuthHeaders() });
      setCustomFields(res.data);
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
    } finally {
      setLoadingCustomFields(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    for (const field of customFields) {
      if (field.required && !newUser.custom_fields[field.id]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }
    
    try {
      await axios.post(`${API}/users`, newUser, { headers: getAuthHeaders() });
      toast.success('User created successfully!');
      setShowCreateDialog(false);
      setNewUser({
        email: '',
        password: '',
        role: 'student',
        first_name: '',
        last_name: '',
        mobile_number: '',
        school_name: '',
        city: '',
        grade: '',
        custom_fields: {}
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };
  
  const handleCreateTeacher = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/users`, newTeacher, { headers: getAuthHeaders() });
      toast.success('Teacher created successfully!');
      setNewTeacher({
        email: '',
        password: '',
        role: 'teacher',
        first_name: '',
        last_name: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create teacher');
    }
  };

  const handleCreateMentor = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/users`, newMentor, { headers: getAuthHeaders() });
      toast.success('Mentor created successfully!');
      setNewMentor({
        email: '',
        password: '',
        role: 'mentor',
        first_name: '',
        last_name: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create mentor');
    }
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setNewUser(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [fieldId]: value
      }
    }));
  };
  
  const handleViewProfile = (userData) => {
    setSelectedUserId(userData.id);
    setShowProfileView(true);
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success(`User ${userName} deleted successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/users/bulk-upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      toast.success(`Successfully uploaded ${res.data.created_count} users`);
      if (res.data.skipped_count > 0) {
        toast.info(`Skipped ${res.data.skipped_count} duplicate users`);
      }
      setFile(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleMentorUpdate = async (e) => {
    e.preventDefault();
    if (!mentorFile) {
      toast.error('Please select a CSV file with mentor mappings');
      return;
    }

    setUploadingMentor(true);
    const formData = new FormData();
    formData.append('file', mentorFile);

    try {
      const res = await axios.post(`${API}/users/bulk-update-mentor`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });

      // Show detailed results
      toast.success(res.data.message);
      
      if (res.data.updated_count > 0) {
        const updatedList = res.data.updated_users.slice(0, 5).map(u => 
          `${u.email}: ${u.old_mentor} → ${u.new_mentor}`
        ).join('\n');
        toast.info(`Sample Updates:\n${updatedList}${res.data.updated_count > 5 ? '\n...' : ''}`, { duration: 8000 });
      }
      
      if (res.data.skipped_count > 0) {
        toast.warning(`${res.data.skipped_count} rows skipped (see console for details)`);
        console.log('Skipped Reasons:', res.data.skipped_reasons);
      }
      
      if (res.data.error_count > 0) {
        toast.error(`${res.data.error_count} errors occurred (see console for details)`);
        console.error('Errors:', res.data.errors);
      }

      setMentorFile(null);
      fetchUsers();
    } catch (error) {
      console.error('Mentor update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update mentors');
    } finally {
      setUploadingMentor(false);
    }
  };

  const findDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const res = await axios.post(`${API}/users/find-duplicates`, {}, { headers: getAuthHeaders() });
      setDuplicates(res.data);
      setShowDuplicates(true);
      if (res.data.length === 0) {
        toast.success('No duplicate users found!');
      }
    } catch (error) {
      toast.error('Failed to find duplicates');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const deleteAllNonAdminUsers = async () => {
    const confirmation = window.prompt(
      'This will delete ALL non-admin users. Type "DELETE ALL" to confirm:'
    );
    
    if (confirmation !== 'DELETE ALL') {
      toast.info('Deletion cancelled');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.delete(`${API}/users/all-non-admins`, { headers: getAuthHeaders() });
      toast.success(`Deleted ${res.data.total_deleted} users (Students: ${res.data.students_deleted}, Teachers: ${res.data.teachers_deleted})`);
      fetchUsers();
    } catch (error) {
      console.error('Delete all users error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatLastLogin = (dateString) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      
      // Convert to IST (Indian Standard Time - Asia/Kolkata)
      // Format: 23 Nov 2025, 17:30 IST
      const dateStr = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
      
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
      });
      
      return `${dateStr}, ${timeStr} IST`;
    } catch (error) {
      console.error('Error formatting last login:', error);
      return 'Invalid date';
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = (
      u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.school_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    let matchesFilter = true;
    if (lastLoginFilter === '7days') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      matchesFilter = u.last_login && new Date(u.last_login) > sevenDaysAgo;
    } else if (lastLoginFilter === '30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      matchesFilter = u.last_login && new Date(u.last_login) > thirtyDaysAgo;
    }

    return matchesSearch && matchesFilter;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let dateA, dateB;
    
    if (sortBy === 'last_login') {
      // Sort by last login timestamp
      dateA = a.last_login ? new Date(a.last_login) : new Date(0);
      dateB = b.last_login ? new Date(b.last_login) : new Date(0);
    } else {
      // Sort by registration date
      dateA = a.registration_date ? new Date(a.registration_date) : new Date(0);
      dateB = b.registration_date ? new Date(b.registration_date) : new Date(0);
    }
    
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  if (showProfileView && selectedUserId) {
    return (
      <StudentProfileView 
        userId={selectedUserId} 
        onClose={() => {
          setShowProfileView(false);
          setSelectedUserId(null);
        }}
      />
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="users">
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-users">
        
        {/* Premium Header */}
        <Card className="border-0 card-shadow bg-white rounded-[32px] overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-[24px] premium-gradient flex items-center justify-center shadow-lg">
                  <UsersIcon className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-navy">Add Users</h1>
                  <p className="text-sm text-[#4D4D4D] mt-1">Create and manage user accounts</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={findDuplicates}
                  variant="outline"
                  size="sm"
                  className="border-2 border-[#E7E7E7] hover:bg-[#F7F7F7] rounded-full px-6"
                  disabled={loadingDuplicates}
                >
                  {loadingDuplicates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                  Find Duplicates
                </Button>
                <Button
                  onClick={deleteAllNonAdminUsers}
                  variant="destructive"
                  size="sm"
                  className="rounded-full px-6"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium Tabs */}
        <Tabs defaultValue="list" className="w-full">
          <TabsList className="inline-flex h-14 items-center justify-center rounded-full bg-white p-1.5 border-0 card-shadow flex-wrap">
            <TabsTrigger 
              value="list" 
              className="rounded-full px-6 py-2.5 data-[state=active]:bg-primary-blue data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              All Users
            </TabsTrigger>
            <TabsTrigger 
              value="add"
              className="rounded-full px-6 py-2.5 data-[state=active]:bg-primary-blue data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Student
            </TabsTrigger>
            <TabsTrigger 
              value="teacher"
              className="rounded-full px-6 py-2.5 data-[state=active]:bg-primary-blue data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Teacher
            </TabsTrigger>
            <TabsTrigger 
              value="mentor"
              className="rounded-full px-6 py-2.5 data-[state=active]:bg-primary-blue data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Mentor
            </TabsTrigger>
            <TabsTrigger 
              value="bulk"
              className="rounded-full px-6 py-2.5 data-[state=active]:bg-primary-blue data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </TabsTrigger>
            <TabsTrigger 
              value="mentor-update"
              className="rounded-full px-6 py-2.5 data-[state=active]:bg-primary-blue data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              Update Mentors
            </TabsTrigger>
          </TabsList>

          {/* All Users Tab */}
          <TabsContent value="list" className="mt-6">
            <Card className="border-0 card-shadow bg-white rounded-[32px] overflow-hidden">
              <CardHeader className="border-b border-[#E7E7E7] p-6">
                <div className="space-y-4">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl font-bold text-navy">All Users</CardTitle>
                      <p className="text-sm text-[#4D4D4D] mt-1">{users.length} total users</p>
                    </div>
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A5A5A5]" />
                      <Input
                        placeholder="Search by name, email, or school..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-11 h-12 border-2 border-[#E7E7E7] rounded-full focus:border-primary-blue shadow-sm"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center space-x-2 bg-[#F7F7F7] rounded-full px-4 py-2">
                      <Filter className="w-4 h-4 text-[#A5A5A5]" />
                      <Select value={lastLoginFilter} onValueChange={(value) => { setLastLoginFilter(value); setCurrentPage(1); }}>
                        <SelectTrigger className="w-44 h-9 border-0 bg-transparent rounded-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="all">All Users</SelectItem>
                          <SelectItem value="7days">Last 7 Days</SelectItem>
                          <SelectItem value="30days">Last 30 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="bg-[#F7F7F7] rounded-full px-4 py-2">
                      <Select value={sortOrder} onValueChange={setSortOrder}>
                        <SelectTrigger className="w-44 h-9 border-0 bg-transparent rounded-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="desc">Newest First</SelectItem>
                          <SelectItem value="asc">Oldest First</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F7F7F7]">
                        <TableHead className="font-semibold text-navy">Name</TableHead>
                        <TableHead className="font-semibold text-navy">Email</TableHead>
                        <TableHead className="font-semibold text-navy">Role</TableHead>
                        <TableHead className="font-semibold text-navy">School</TableHead>
                        <TableHead 
                          className="font-semibold text-navy cursor-pointer hover:text-primary-blue transition-colors"
                          onClick={() => {
                            if (sortBy === 'last_login') {
                              setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                            } else {
                              setSortBy('last_login');
                              setSortOrder('desc');
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Last Login</span>
                            {sortBy === 'last_login' && (
                              sortOrder === 'desc' ? 
                                <ArrowDown className="w-4 h-4" /> : 
                                <ArrowUp className="w-4 h-4" />
                            )}
                            {sortBy !== 'last_login' && <ArrowUpDown className="w-4 h-4 opacity-30" />}
                          </div>
                        </TableHead>
                        <TableHead className="text-right font-semibold text-navy">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedUsers.map((u) => (
                        <TableRow key={u.id} className="hover:bg-[#F7F7F7] transition-colors">
                          <TableCell className="font-medium text-navy">{u.first_name} {u.last_name}</TableCell>
                          <TableCell className="text-[#4D4D4D]">{u.email}</TableCell>
                          <TableCell>
                            <Badge className="bg-primary-blue/10 text-primary-blue border-0">{u.role}</Badge>
                          </TableCell>
                          <TableCell className="text-[#4D4D4D]">{u.school_name || '-'}</TableCell>
                          <TableCell className="text-[#4D4D4D]">
                            <div className="flex flex-col">
                              <span className="font-medium">{formatLastLogin(u.last_login)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-9 border-2 border-[#E7E7E7] rounded-full px-4"
                                onClick={() => handleViewProfile(u)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {u.role === 'mentor' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-9 border-2 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-full px-4"
                                  onClick={() => {
                                    setSelectedMentorForAssignment(u);
                                    setShowAssignDialog(true);
                                  }}
                                >
                                  <UsersIcon className="w-4 h-4 mr-1" />
                                  Assign Students
                                </Button>
                              )}
                              {u.role !== 'admin' && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="destructive" size="sm" className="h-9 w-9 rounded-full p-0">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="rounded-[32px]">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete {u.first_name} {u.last_name}? This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)} className="bg-red-600 hover:bg-red-700 rounded-full">
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-[#E7E7E7]">
                    <p className="text-sm text-[#4D4D4D]">
                      Showing {((currentPage - 1) * usersPerPage) + 1} to {Math.min(currentPage * usersPerPage, sortedUsers.length)} of {sortedUsers.length} users
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full px-6"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full px-6"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add Manually Tab */}
          <TabsContent value="add" className="mt-6">
            <Card className="border-0 card-shadow bg-white rounded-[32px] max-w-3xl mx-auto overflow-hidden">
              <CardHeader className="border-b border-[#E7E7E7] p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-[20px] premium-gradient flex items-center justify-center shadow-lg">
                    <PlusCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-navy">Add User Manually</CardTitle>
                    <p className="text-sm text-[#4D4D4D] mt-1">Create a single user account</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleCreateUser} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">First Name</Label>
                      <Input
                        value={newUser.first_name}
                        onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Last Name</Label>
                      <Input
                        value={newUser.last_name}
                        onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Email</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        required
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Password</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Role</Label>
                      <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                        <SelectTrigger className="h-11 border-2 border-[#E7E7E7] rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="teacher">Teacher</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Mobile Number</Label>
                      <Input
                        value={newUser.mobile_number}
                        onChange={(e) => setNewUser({ ...newUser, mobile_number: e.target.value })}
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">School Name</Label>
                      <Input
                        value={newUser.school_name}
                        onChange={(e) => setNewUser({ ...newUser, school_name: e.target.value })}
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">City</Label>
                      <Input
                        value={newUser.city}
                        onChange={(e) => setNewUser({ ...newUser, city: e.target.value })}
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    {newUser.role === 'student' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-navy">Grade</Label>
                        <Input
                          value={newUser.grade}
                          onChange={(e) => setNewUser({ ...newUser, grade: e.target.value })}
                          className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                        />
                      </div>
                    )}
                  </div>

                  {!loadingCustomFields && customFields.length > 0 && (
                    <div className="pt-6 border-t border-[#E7E7E7]">
                      <h3 className="text-lg font-bold text-navy mb-4">Additional Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {customFields.map(field => (
                          <div key={field.id} className="space-y-2">
                            <Label className="text-sm font-semibold text-navy flex items-center space-x-2">
                              <span>{field.label}</span>
                              {field.required && <span className="text-red-500">*</span>}
                              {field.visibility === 'admin_only' && (
                                <Badge variant="secondary" className="text-xs">Admin Only</Badge>
                              )}
                            </Label>
                            
                            {(field.field_type === 'text' || field.field_type === 'email' || field.field_type === 'phone') && (
                              <Input
                                type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
                                value={newUser.custom_fields[field.id] || ''}
                                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                placeholder={field.placeholder || ''}
                                required={field.required}
                                className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                              />
                            )}
                            
                            {field.field_type === 'number' && (
                              <Input
                                type="number"
                                value={newUser.custom_fields[field.id] || ''}
                                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                placeholder={field.placeholder || ''}
                                required={field.required}
                                className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                              />
                            )}
                            
                            {field.field_type === 'date' && (
                              <Input
                                type="date"
                                value={newUser.custom_fields[field.id] || ''}
                                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                required={field.required}
                                className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                              />
                            )}
                            
                            {field.field_type === 'dropdown' && (
                              <Select 
                                value={newUser.custom_fields[field.id] && newUser.custom_fields[field.id] !== '' ? newUser.custom_fields[field.id] : undefined} 
                                onValueChange={(value) => handleCustomFieldChange(field.id, value)}
                              >
                                <SelectTrigger className="h-11 border-2 border-[#E7E7E7] rounded-xl">
                                  <SelectValue placeholder={field.placeholder || 'Select an option'} />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.dropdown_options?.map((option, idx) => (
                                    <SelectItem key={idx} value={option}>{option}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button type="submit" className="w-full h-14 bg-primary-blue hover:bg-primary-blue/90 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all">
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Create User Account
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add Teacher Tab */}
          <TabsContent value="teacher" className="mt-6">
            <Card className="border-0 card-shadow bg-white rounded-[32px] max-w-3xl mx-auto overflow-hidden">
              <CardHeader className="border-b border-[#E7E7E7] p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-[20px] premium-gradient flex items-center justify-center shadow-lg">
                    <PlusCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-navy">Add Teacher</CardTitle>
                    <p className="text-sm text-[#4D4D4D] mt-1">Create a teacher account</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleCreateTeacher} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">First Name</Label>
                      <Input
                        value={newTeacher.first_name}
                        onChange={(e) => setNewTeacher({ ...newTeacher, first_name: e.target.value })}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Last Name</Label>
                      <Input
                        value={newTeacher.last_name}
                        onChange={(e) => setNewTeacher({ ...newTeacher, last_name: e.target.value })}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Email</Label>
                      <Input
                        type="email"
                        value={newTeacher.email}
                        onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                        required
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Password</Label>
                      <Input
                        type="password"
                        value={newTeacher.password}
                        onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })}
                        required
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-14 bg-primary-blue hover:bg-primary-blue/90 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all">
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Create Teacher Account
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add Mentor Tab */}
          <TabsContent value="mentor" className="mt-6">
            <Card className="border-0 card-shadow bg-white rounded-[32px] max-w-3xl mx-auto overflow-hidden">
              <CardHeader className="border-b border-[#E7E7E7] p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-[20px] premium-gradient flex items-center justify-center shadow-lg">
                    <PlusCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-navy">Add Mentor</CardTitle>
                    <p className="text-sm text-[#4D4D4D] mt-1">Create a mentor account</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleCreateMentor} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">First Name</Label>
                      <Input
                        value={newMentor.first_name}
                        onChange={(e) => setNewMentor({ ...newMentor, first_name: e.target.value })}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Last Name</Label>
                      <Input
                        value={newMentor.last_name}
                        onChange={(e) => setNewMentor({ ...newMentor, last_name: e.target.value })}
                        required
                        className="h-12 border-2 border-[#E7E7E7] rounded-2xl focus:border-primary-blue shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Email</Label>
                      <Input
                        type="email"
                        value={newMentor.email}
                        onChange={(e) => setNewMentor({ ...newMentor, email: e.target.value })}
                        required
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-navy">Password</Label>
                      <Input
                        type="password"
                        value={newMentor.password}
                        onChange={(e) => setNewMentor({ ...newMentor, password: e.target.value })}
                        required
                        className="h-11 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-14 bg-primary-blue hover:bg-primary-blue/90 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all">
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Create Mentor Account
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Upload Tab */}
          <TabsContent value="bulk" className="mt-6">
            <Card className="border-0 card-shadow bg-white rounded-2xl max-w-3xl mx-auto">
              <CardHeader className="border-b border-[#E7E7E7]">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-xl premium-gradient flex items-center justify-center">
                    <Upload className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-navy">Bulk Upload Users</CardTitle>
                    <p className="text-sm text-[#4D4D4D] mt-1">Import multiple users from CSV or Excel</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="p-4 rounded-xl bg-[#F7F7F7] border border-[#E7E7E7]">
                  <h4 className="font-semibold text-navy mb-2">Required Columns:</h4>
                  <code className="text-xs block bg-white p-3 rounded-lg border border-[#E7E7E7]">
                    First Name, Last name, School Name, Grade, Mobile Number, Registration date, Fees paid (amount), Fees remaining (amount), Mentor, Email id, Password
                  </code>
                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-[#4D4D4D]">✓ Supports up to 1000 users per upload</p>
                    <p className="text-xs text-[#4D4D4D]">✓ Accepts .csv, .xlsx, and .xls files</p>
                  </div>
                </div>

                {!loadingCustomFields && customFields.length > 0 && (
                  <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                    <h4 className="font-semibold text-navy mb-3 flex items-center space-x-2">
                      <span>📋 Available Custom Fields</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      {customFields.map(field => (
                        <div key={field.id} className="text-sm bg-white p-2 rounded-lg border border-blue-200">
                          <span className="font-medium text-navy">{field.label}</span>
                          <span className="text-[#A5A5A5] text-xs ml-2">({field.field_type})</span>
                          {field.visibility === 'admin_only' && (
                            <Badge variant="secondary" className="ml-2 text-xs">Admin Only</Badge>
                          )}
                          {field.required && (
                            <span className="ml-2 text-xs text-red-600 font-medium">* Required</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <p className="text-xs text-navy font-medium mb-1">💡 Tips:</p>
                      <ul className="text-xs text-[#4D4D4D] space-y-1 ml-4 list-disc">
                        <li>Use the exact field label as column name</li>
                        <li>Custom fields are optional</li>
                        <li>Empty cells will leave the field unset</li>
                      </ul>
                    </div>
                  </div>
                )}

                <form onSubmit={handleFileUpload} className="space-y-4">
                  <div className="border-2 border-dashed border-[#E7E7E7] rounded-xl p-8 text-center hover:border-primary-blue transition-colors">
                    <Upload className="w-12 h-12 mx-auto mb-4 text-[#A5A5A5]" />
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="max-w-xs mx-auto"
                    />
                    {file && <p className="text-sm text-navy mt-2 font-medium">{file.name}</p>}
                  </div>

                  {uploading && (
                    <div className="space-y-2">
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-sm text-center text-[#4D4D4D]">{uploadProgress}% uploaded</p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-primary-blue hover:bg-primary-blue/90 text-white font-semibold rounded-xl"
                    disabled={uploading || !file}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Upload Users
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Update Mentor Tab */}
          <TabsContent value="mentor-update" className="mt-6">
            <Card className="border-0 card-shadow bg-white rounded-2xl max-w-3xl mx-auto">
              <CardHeader className="border-b border-[#E7E7E7]">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <UsersIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-navy">Bulk Update Mentor Assignments</CardTitle>
                    <p className="text-sm text-[#4D4D4D] mt-1">Update mentor field for existing students from CSV</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="p-4 rounded-xl bg-purple-50 border-2 border-purple-200">
                  <h4 className="font-semibold text-navy mb-3 flex items-center space-x-2">
                    <span>📋 How It Works</span>
                  </h4>
                  <div className="space-y-2 text-sm text-[#4D4D4D]">
                    <p>This tool updates the <strong>mentor field</strong> for existing students without creating new users or modifying other data.</p>
                    <div className="bg-white p-3 rounded-lg border border-purple-200 mt-3">
                      <p className="font-semibold text-navy mb-2">✓ What happens:</p>
                      <ul className="space-y-1 ml-4 list-disc text-xs">
                        <li>Finds students by email (case-insensitive)</li>
                        <li>Updates ONLY their mentor field</li>
                        <li>Skips non-existent students</li>
                        <li>Normalizes mentor names to lowercase</li>
                        <li>Safe to run multiple times</li>
                      </ul>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-lg mt-2">
                      <p className="font-semibold text-navy mb-2">⚡ Mentor Routing Logic:</p>
                      <div className="text-xs space-y-1">
                        <p>• Student with <code className="bg-white px-2 py-0.5 rounded">mentor: "shreya"</code> → visible to <code className="bg-white px-2 py-0.5 rounded">shreya@gdiot.in</code></p>
                        <p>• Student with <code className="bg-white px-2 py-0.5 rounded">mentor: "piyusha"</code> → visible to <code className="bg-white px-2 py-0.5 rounded">piyusha@gdiot.in</code></p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-[#F7F7F7] border border-[#E7E7E7]">
                  <h4 className="font-semibold text-navy mb-2">Required CSV Columns:</h4>
                  <code className="text-xs block bg-white p-3 rounded-lg border border-[#E7E7E7]">
                    Email id, Mentor
                  </code>
                  <div className="mt-3 space-y-1">
                    <p className="text-xs text-[#4D4D4D]"><strong>Email id:</strong> Student's email address</p>
                    <p className="text-xs text-[#4D4D4D]"><strong>Mentor:</strong> Mentor name (e.g., shreya, piyusha, mrunal)</p>
                    <p className="text-xs text-green-600 mt-2">✓ Accepts .csv, .xlsx, and .xls files</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                  <h4 className="font-semibold text-navy mb-2">📝 CSV Example:</h4>
                  <div className="bg-white p-3 rounded-lg border border-blue-200 overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 font-semibold">Email id</th>
                          <th className="text-left p-2 font-semibold">Mentor</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2">student1@example.com</td>
                          <td className="p-2">shreya</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">student2@example.com</td>
                          <td className="p-2">piyusha</td>
                        </tr>
                        <tr>
                          <td className="p-2">student3@example.com</td>
                          <td className="p-2">mrunal</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <form onSubmit={handleMentorUpdate} className="space-y-4">
                  <div className="border-2 border-dashed border-[#E7E7E7] rounded-xl p-8 text-center hover:border-purple-500 transition-colors">
                    <UsersIcon className="w-12 h-12 mx-auto mb-4 text-purple-500" />
                    <Input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setMentorFile(e.target.files[0])}
                      className="max-w-xs mx-auto"
                    />
                    {mentorFile && <p className="text-sm text-navy mt-2 font-medium">{mentorFile.name}</p>}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-semibold rounded-xl"
                    disabled={uploadingMentor || !mentorFile}
                  >
                    {uploadingMentor ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Updating Mentors...
                      </>
                    ) : (
                      <>
                        <UsersIcon className="w-5 h-5 mr-2" />
                        Update Mentor Assignments
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Duplicates Dialog */}
        <Dialog open={showDuplicates} onOpenChange={setShowDuplicates}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2 text-navy">
                <AlertTriangle className="w-5 h-5" />
                <span>Duplicate Users Found ({duplicates.length})</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {duplicates.length === 0 ? (
                <p className="text-center py-8 text-[#4D4D4D]">No duplicates found!</p>
              ) : (
                duplicates.map((dup, idx) => (
                  <Card key={idx} className="border-2 border-[#E7E7E7]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-navy">{dup.email}</p>
                          <p className="text-sm text-[#4D4D4D]">{dup.count} duplicates found</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Assign Students Dialog */}
      <AssignStudentsDialog
        mentor={selectedMentorForAssignment}
        isOpen={showAssignDialog}
        onClose={() => {
          setShowAssignDialog(false);
          setSelectedMentorForAssignment(null);
        }}
        onAssignmentComplete={() => {
          fetchUsers(); // Refresh user list
        }}
      />
    </Layout>
  );
}
